// src/modules/competitions/pages/JudgeParticipantsList.tsx

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Card from '../../../shared/components/ui/Card';
import Button from '../../../shared/components/ui/Button';
import Loading from '../../../shared/components/Loading';
import ErrorMessage from '../../../shared/components/ErrorMessage';
import ParticipantCard from '../components/ParticipantCard';
import { judgeService } from '../services/judgeService';
import { ParticipantDetails, CategoryJudgeStatus } from '../types/judge';
import '../styles/judgeParticipantsList.css';

const JudgeParticipantsList: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const navigationState = location.state as {
    categoryId?: number;
    level?: string;
    categoryName?: string;
  } | null;

  const [eventTitle, setEventTitle] = useState('');
  const [categories, setCategories] = useState<CategoryJudgeStatus[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(
    navigationState?.categoryId || null
  );
  const [selectedLevel, setSelectedLevel] = useState<string>(
    navigationState?.level || ''
  );
  // ✅ Usar tipo any[] porque los datos vienen directos del endpoint
  const [participants, setParticipants] = useState<any[]>([]);
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'evaluated'>('all');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (eventId) {
      loadJudgeStatus();
    }
  }, [eventId]);

  useEffect(() => {
    if (!selectedCategory || !selectedLevel) {
      if (!navigationState?.categoryId || !navigationState?.level) {
        navigate(`/judge/evaluations/${eventId}`, { replace: true });
      }
    }
  }, [selectedCategory, selectedLevel, navigationState, eventId, navigate]);

  useEffect(() => {
    if (selectedCategory && selectedLevel) {
      loadParticipants();
    }
  }, [selectedCategory, selectedLevel, statusFilter]);

  const loadJudgeStatus = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await judgeService.getStatusByEvent(Number(eventId));

      if (response.success && response.data) {
        setEventTitle(response.data.event_title);
        setCategories(response.data.categories);
      } else {
        setError(response.message || 'Error al cargar estado');
      }
    } catch (err) {
      setError('Error al cargar información del juez');
      console.error('Error en loadJudgeStatus:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadParticipants = async () => {
    if (!selectedCategory || !selectedLevel) return;

    try {
      setIsLoading(true);
      setError(null);

      // ✅ Obtener datos directos del endpoint
      const response = await judgeService.getParticipantsWithMedia(
        selectedCategory,
        selectedLevel,
        statusFilter
      );

      if (response.success && response.data) {
        setParticipants(response.data.participants);
      } else {
        setError(response.message || 'Error al cargar participantes');
      }
    } catch (err) {
      setError('Error al cargar participantes');
      console.error('Error en loadParticipants:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ Pasar datos completos al hijo por state
  const handleEvaluateParticipant = (participantId: number) => {
    const participantData = participants.find(
      p => p.participant_id === participantId
    );
    
    if (participantData) {
      // ✅ Transformar al formato ParticipantDetails que espera el hijo
      const participantDetails = {
        participant: {
          participant_id: participantData.participant_id,
          participant_code: participantData.participant_code,
          level: participantData.level,
          competition_category_id: participantData.category_id,
          category_name: participantData.category_name,
          event_title: participantData.event_title
        },
        items: participantData.items,
        statistics: participantData.statistics
      };
      
      console.log('📤 Datos enviados al hijo:', participantDetails); // Debug
      
      navigate(`/competitions/judge/${eventId}/participant/${participantId}`, {
        state: { participantDetails: participantDetails }  // ✅ Asegurar que se pasa correctamente
      });
    } else {
      console.error('❌ No se encontró participante con ID:', participantId);
    }
  };

  if (isLoading && categories.length === 0) {
    return <Loading message="Cargando panel de juez..." />;
  }

  return (
    <div className="judge-participants-list-page">
      <Card variant="glass" className="judge-header">
        <h1>🏆 Panel de Juez - Evaluaciones</h1>
        <h2>{eventTitle}</h2>

        {error && <ErrorMessage message={error} type="error" onClose={() => setError(null)} />}
      </Card>

      {selectedCategory && selectedLevel ? (
        <>
          <Card variant="glass" className="participants-navigation-card">
            <Button
              variant="outline"
              onClick={() => navigate(`/judge/evaluations/${eventId}`)}
            >
              ← Volver a categorías
            </Button>

            <div className="participants-header">
              <h3 className="participants-title">
                {navigationState?.categoryName || categories.find(c => c.category_id === selectedCategory)?.category_name} - {selectedLevel}
              </h3>

              <div className="participants-filters">
                <button
                  className={`filter-btn ${statusFilter === 'all' ? 'active' : ''}`}
                  onClick={() => setStatusFilter('all')}
                >
                  Todos
                </button>
                <button
                  className={`filter-btn ${statusFilter === 'pending' ? 'active' : ''}`}
                  onClick={() => setStatusFilter('pending')}
                >
                  Pendientes
                </button>
                <button
                  className={`filter-btn ${statusFilter === 'evaluated' ? 'active' : ''}`}
                  onClick={() => setStatusFilter('evaluated')}
                >
                  Evaluados
                </button>
              </div>
            </div>
          </Card>

          {isLoading ? (
            <Loading message="Cargando participantes..." />
          ) : participants.length === 0 ? (
            <Card variant="flat">
              <div className="no-participants">
                <span className="empty-icon">📋</span>
                <h3>No hay participantes</h3>
                <p>
                  {statusFilter === 'all' && 'No hay participantes en este nivel'}
                  {statusFilter === 'pending' && 'No hay participantes pendientes de evaluación'}
                  {statusFilter === 'evaluated' && 'No hay participantes evaluados'}
                </p>
              </div>
            </Card>
          ) : (
            <div className="participants-collage-grid">
              {participants.map(participant => (
                <ParticipantCard
                  key={participant.participant_id}
                  participant={{
                    participant_id: participant.participant_id,
                    participant_code: participant.participant_code,
                    level: participant.level,
                    registration_date: participant.registration_date || '',
                    statistics: participant.statistics,
                    first_image_url: participant.first_image_url
                  }}
                  onEvaluate={handleEvaluateParticipant}
                />
              ))}
            </div>
          )}
        </>
      ) : (
        <Loading message="Cargando..." />
      )}
    </div>
  );
};

export default JudgeParticipantsList;